import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import {
  Package, Search, AlertTriangle, CheckCircle2, Archive, Clock, Factory
} from 'lucide-react';

const statusConfig = {
  'On Track': { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'At Risk': { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  'Delayed': { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
  'Completed': { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
};

const tabs = [
  { key: 'active', label: 'Active', icon: Package },
  { key: 'completed', label: 'Completed', icon: CheckCircle2 },
];

export default function InternalDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('active');
  const [factoryName, setFactoryName] = useState('');

  const fetchOrders = useCallback(async (lifecycle) => {
    setLoading(true);
    try {
      const res = await api.get(`/orders?lifecycle=${lifecycle}`);
      setOrders(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [statsRes, profileRes] = await Promise.all([
          api.get('/dashboard/stats'),
          api.get('/profile'),
        ]);
        setStats(statsRes.data);
        if (profileRes.data.factory_id) {
          try {
            const factoriesRes = await api.get('/factories');
            const myFactory = factoriesRes.data.find(f => f.id === profileRes.data.factory_id);
            if (myFactory) setFactoryName(myFactory.name);
          } catch {}
        }
      } catch (e) { console.error(e); }
    };
    loadData();
    fetchOrders(activeTab);
  }, [fetchOrders, activeTab]);

  const filtered = orders.filter(o => {
    if (search && !o.style_name?.toLowerCase().includes(search.toLowerCase()) && !o.order_id?.toLowerCase().includes(search.toLowerCase()) && !o.buyer_name?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterStatus !== 'all' && o.status !== filterStatus) return false;
    return true;
  }).sort((a, b) => {
    const dateA = a.delivery_date ? new Date(a.delivery_date) : new Date('9999-12-31');
    const dateB = b.delivery_date ? new Date(b.delivery_date) : new Date('9999-12-31');
    return dateA - dateB;
  });

  const statCards = [
    { label: 'Total Orders', value: stats.total_orders || 0, icon: Package, color: 'text-[#1C1C1C]' },
    { label: 'On Track', value: stats.on_track || 0, icon: CheckCircle2, color: 'text-emerald-600' },
    { label: 'At Risk', value: stats.at_risk || 0, icon: AlertTriangle, color: 'text-amber-600' },
    { label: 'Delayed', value: stats.delayed || 0, icon: AlertTriangle, color: 'text-red-600' },
  ];

  return (
    <DashboardLayout>
      <div data-testid="internal-dashboard" className="space-y-8">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[#0A0A0A] tracking-tight">Dashboard</h1>
          <p className="text-sm text-[#52525B] mt-1">
            {factoryName ? (
              <span className="inline-flex items-center gap-1.5"><Factory className="w-3.5 h-3.5 text-cyan-600" />{factoryName} — Production & Operations</span>
            ) : 'Manage assigned production orders'}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((s) => (
            <div key={s.label} className="bg-white rounded-md border border-black/[0.06] p-4">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA]">{s.label}</p>
                <s.icon className={`w-3.5 h-3.5 ${s.color}`} />
              </div>
              <p className="font-heading text-2xl font-semibold text-[#0A0A0A] mt-1">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-white rounded-md border border-black/[0.06] p-1 w-fit">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === tab.key ? 'bg-[#1C1C1C] text-white' : 'text-[#52525B] hover:bg-[#F5F5F5]'}`}
              data-testid={`internal-tab-${tab.key}`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
            <Input placeholder="Search by PO, style, or buyer..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-10 rounded-md border-black/[0.08]" data-testid="internal-search-input" />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[160px] h-10 rounded-md border-black/[0.08]" data-testid="internal-filter-status">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="On Track">On Track</SelectItem>
              <SelectItem value="At Risk">At Risk</SelectItem>
              <SelectItem value="Delayed">Delayed</SelectItem>
              <SelectItem value="Completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-md border border-black/[0.06] overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-6 h-6 border-2 border-[#1C1C1C] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-black/[0.04] hover:bg-transparent">
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">PA</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">Style</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11 hidden md:table-cell">Buyer</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11 hidden lg:table-cell">Factory</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11 hidden lg:table-cell">Qty</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11 hidden lg:table-cell">Delivery</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">Progress</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((order) => {
                  const sc = statusConfig[order.status] || statusConfig['On Track'];
                  return (
                    <TableRow key={order.id} className="table-row-hover cursor-pointer border-b border-black/[0.03]" onClick={() => navigate(`/dashboard/internal/orders/${order.id}`)} data-testid={`internal-order-row-${order.order_id}`}>
                      <TableCell className="text-sm font-semibold text-[#0A0A0A]">{order.order_id}</TableCell>
                      <TableCell>
                        <p className="text-sm font-medium text-[#0A0A0A] truncate max-w-[200px]">{order.style_name}</p>
                        {order.department && <p className="text-[11px] text-[#A1A1AA]">{order.department}</p>}
                      </TableCell>
                      <TableCell className="text-sm text-[#52525B] hidden md:table-cell">{order.buyer_name}</TableCell>
                      <TableCell className="text-sm text-[#52525B] hidden lg:table-cell">{order.factory_name || '-'}</TableCell>
                      <TableCell className="text-sm text-[#52525B] hidden lg:table-cell">{order.quantity?.toLocaleString()}</TableCell>
                      <TableCell className="text-sm text-[#52525B] hidden lg:table-cell">
                        {order.delivery_date ? new Date(order.delivery_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 min-w-[100px]">
                          <Progress value={order.progress} className={`h-1.5 bg-black/[0.06] flex-1 ${order.status === 'Delayed' ? '[&>div]:bg-red-500' : order.status === 'At Risk' ? '[&>div]:bg-amber-500' : '[&>div]:bg-emerald-500'}`} />
                          <span className="text-xs font-semibold text-[#0A0A0A] w-8 text-right">{order.progress}%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className={`status-badge ${sc.bg} ${sc.text}`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                          {order.status}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-16 text-sm text-[#A1A1AA]">
                      {activeTab === 'active' ? 'No active orders assigned to you.' : 'No completed orders.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
